import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Text, IconButton, Tooltip, HStack, VStack, useDisclosure, useBreakpointValue, Button, useToast, Input, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, FormControl, FormLabel } from '@chakra-ui/react';
import { FaEdit, FaPen, FaClock } from 'react-icons/fa';
import { useNotes, GLOBAL_CHARACTER_LIMIT } from '../context/NotesContext';
import { useAudioContext } from '../context/AudioContext';
import { useDisplayNames } from '../context/DisplayNamesContext';
import NotesModal from './NotesModal';
import TooltipKeyBox from './TooltipKeyBox';

// Extend Window interface for global expanded note panel reference
declare global {
  interface Window {
    __expandedNotePanel?: {
      trackNumber: number;
      startEditing: () => void;
      startEditingAndInsertTimestamp: () => void;
      startEditingAndInsertChord: () => void;
      insertTimestampAtEnd: () => void;
      isEditing: boolean;
      highlightTimestampAtTime: (time: number) => void;
    };
  }
}

interface ExpandedNotePanelProps {
  trackNumber: number;
  fileId?: string;
}
const ExpandedNotePanel: React.FC<ExpandedNotePanelProps> = ({ trackNumber, fileId }) => {
  const { getNote, addNote, getTotalCharacterCount, getRemainingCharacters, canAddCharacters, trackNotes } = useNotes();
  const { audioElements, isPlaying, setLastStartPositions, selectedPlayer } = useAudioContext();
  const { getCustomDisplayName, setDisplayName: saveDisplayName, hasCustomDisplayName } = useDisplayNames();
  const note = fileId ? getNote(fileId) : '';
  const isMobile = useBreakpointValue({ base: true, md: false });
  const contentRef = useRef<HTMLDivElement>(null);
  const editableRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [originalContent, setOriginalContent] = useState(''); // Store original content for cancel
  const [displayName, setDisplayName] = useState('');
  const [liveCharCount, setLiveCharCount] = useState(0);
  const isTextStampFromHotkeyRef = useRef(false);
  const isSavingRef = useRef(false); // Prevent double saves
  const isFinishingTextStampRef = useRef(false); // Prevent blur handler from firing during text stamp finish
  const toast = useToast();
  
  // Refs for optimized timestamp highlighting
  const sortedTimestampsRef = useRef<{ element: HTMLElement; time: number; type: 'chord' | 'timestamp' }[]>([]);
  const currentHighlightedIndexRef = useRef<number>(-1);
  const cacheRebuildTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get track title from audio element
  const audio = audioElements.get(trackNumber);
  const trackTitle = audio?.title || `Track ${trackNumber}`;

  // Calculate live character count including current editor content
  const calculateLiveCharCount = (currentEditorContent: string) => {
    // Get total count of all saved notes except current file
    const totalWithoutCurrent = Object.entries(trackNotes)
      .filter(([key]) => fileId && key !== fileId)
      .reduce((total, [, noteContent]) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = noteContent;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        return total + plainText.length;
      }, 0);

    // Add current editor content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentEditorContent;
    const currentPlainText = tempDiv.textContent || tempDiv.innerText || '';
    
    return totalWithoutCurrent + currentPlainText.length;
  };

  // Initialize display name
  useEffect(() => {
    const customName = fileId ? getCustomDisplayName(fileId) : '';
    setDisplayName(customName || trackTitle);
  }, [fileId, trackTitle, getCustomDisplayName]);

  // Initialize live character count when note changes
  useEffect(() => {
    setLiveCharCount(calculateLiveCharCount(note));
  }, [note, trackNotes, fileId]);

  // Handle display name changes
  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayName(e.target.value);
  };

  const handleDisplayNameSave = () => {
    if (!fileId) return;
    const trimmedName = displayName.trim();
    if (trimmedName !== trackTitle) {
      saveDisplayName(fileId, trimmedName);
    } else {
      // If it's the same as original, remove custom name
      saveDisplayName(fileId, '');
    }
  };

  const handleDisplayNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDisplayNameSave();
      nameInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Revert to saved name
      const customName = fileId ? getCustomDisplayName(fileId) : '';
      setDisplayName(customName || trackTitle);
      nameInputRef.current?.blur();
    }
  };
  
  // Responsive max height - smaller on mobile to avoid obscuring players
  // Player height is approximately 80px on mobile, so 1.5x = 120px, but we need less to avoid obscuring
  const maxHeight = useBreakpointValue({ base: "75px", md: "200px" });
  const containerHeight = useBreakpointValue({ base: "120px", md: "240px" });
  
  // Handle double-click to start editing (desktop only)
  const handleDoubleClick = () => {
    if (!isEditing && !isMobile) {
      startEditing();
    }
  };

  // Start editing mode (can be called externally)
  const startEditing = () => {
    setEditContent(note);
    setOriginalContent(note); // Store original for cancel
    setLiveCharCount(calculateLiveCharCount(note));
    setIsEditing(true);
    // Focus the editable area after it renders
    setTimeout(() => {
      if (editableRef.current) {
        // Set content directly via DOM manipulation (like NotesModal)
        editableRef.current.innerHTML = note;
        editableRef.current.focus();
        // Place cursor at end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 50);
  };

  // Insert timestamp at end with line break if needed (for global hotkey)
  const insertTimestampAtEnd = () => {
    if (editableRef.current) {
      // Check if the editor is currently focused
      const isEditorFocused = document.activeElement === editableRef.current;
      
      // Check for meaningful content
      const hasContent = hasMeaningfulContent(editableRef.current);
      
      // If there's no meaningful content, clean up any empty elements (like <br> tags left by browser)
      if (!hasContent) {
        // Remove all <br> tags and empty divs
        const brElements = editableRef.current.querySelectorAll('br');
        brElements.forEach(br => br.remove());
        const emptyDivs = Array.from(editableRef.current.querySelectorAll('div')).filter(div => {
          const text = div.textContent?.trim() || '';
          return text === '' && div.children.length === 0;
        });
        emptyDivs.forEach(div => div.remove());
        // Also check if the editor itself only contains empty elements
        if (editableRef.current.childNodes.length === 0 || 
            (editableRef.current.childNodes.length === 1 && editableRef.current.firstChild?.nodeName === 'BR')) {
          editableRef.current.innerHTML = '';
        }
      }
      
      // Ensure cursor is positioned at the end of the content
      const range = document.createRange();
      const selection = window.getSelection();
      
      // Move cursor to the very end of the editor content
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
      
      // Add a line break if editor is NOT focused AND there's existing meaningful content
      if (!isEditorFocused && hasContent) {
        editableRef.current.appendChild(document.createElement('br'));
        // Update the range to be after the new line break
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      // Call the existing insertTimestamp function which handles everything properly
      insertTimestamp();
    }
  };

  // Start editing and insert timestamp (for global hotkey)
  const startEditingAndInsertTimestamp = () => {
    if (!isEditing) {
      setEditContent(note);
      setOriginalContent(note); // Store original for cancel
      setLiveCharCount(calculateLiveCharCount(note));
      setIsEditing(true);
      // Focus and insert timestamp after it renders
      setTimeout(() => {
        if (editableRef.current) {
          // Set content directly via DOM manipulation (like NotesModal)
          editableRef.current.innerHTML = note;
          editableRef.current.focus();
          
          // Place cursor on a new line after existing content (like NotesModal)
          if (note && editableRef.current.childNodes.length > 0) {
            // Check if last node is a <br> (new line), if not, add one
            let lastNode = editableRef.current.lastChild;
            if (!lastNode || lastNode.nodeName !== 'BR') {
              const br = document.createElement('br');
              editableRef.current.appendChild(br);
              lastNode = br;
            }
            // Place cursor after the <br>
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStartAfter(lastNode);
            range.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(range);
          } else {
            // No existing content, just place cursor at end
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(editableRef.current);
            range.collapse(false);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
          
          // Insert timestamp after another small delay
          setTimeout(() => {
            insertTimestamp();
          }, 10);
        }
      }, 50);
    } else {
      // Already editing, just insert timestamp
      insertTimestamp();
    }
  };

  // Attach event handlers for timestamps and chords
  const attachEventHandlers = () => {
    if (!contentRef.current) return;
    
    // Remove existing handlers first to avoid duplicates
    contentRef.current.removeEventListener('click', handleClick);
    contentRef.current.removeEventListener('dblclick', handleTimestampDoubleClick);
    contentRef.current.removeEventListener('touchend', handleTouchEnd);
    
    // Add event handlers
    contentRef.current.addEventListener('click', handleClick);
    contentRef.current.addEventListener('dblclick', handleTimestampDoubleClick);
    contentRef.current.addEventListener('touchend', handleTouchEnd);
  };

  // Click handler for timestamps and chords
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
      e.preventDefault();
      const timestamp = target.textContent?.replace(/[[\]]/g, '');
      if (timestamp) {
        // For chords, we need to get the timestamp from the data attribute
        let timeInSeconds;
        if (target.classList.contains('chord')) {
          timeInSeconds = parseFloat(target.getAttribute('data-timestamp') || '0');
        } else {
          const [minutes, seconds] = timestamp.split(':').map(Number);
          timeInSeconds = minutes * 60 + seconds;
        }
        
        // Set time for all audio elements
        audioElements.forEach(audio => {
          audio.currentTime = timeInSeconds;
        });
        
        // Update last start positions if playing
        if (isPlaying) {
          const newStartPositions = new Map<number, number>();
          audioElements.forEach((audio, playerNumber) => {
            newStartPositions.set(playerNumber, audio.currentTime);
          });
          setLastStartPositions(newStartPositions);
        }
      }
    }
  };

  // Double-click handler for editing timestamps and chords
  const handleTimestampDoubleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
      e.preventDefault();
      e.stopPropagation();
      editTextStamp(target);
    }
  };

  // Touch handler for mobile devices
  const handleTouchEnd = (e: TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
      e.preventDefault();
      const timestamp = target.textContent?.replace(/[[\]]/g, '');
      if (timestamp) {
        // For chords, we need to get the timestamp from the data attribute
        let timeInSeconds;
        if (target.classList.contains('chord')) {
          timeInSeconds = parseFloat(target.getAttribute('data-timestamp') || '0');
        } else {
          const [minutes, seconds] = timestamp.split(':').map(Number);
          timeInSeconds = minutes * 60 + seconds;
        }
        
        // Set time for all audio elements
        audioElements.forEach(audio => {
          audio.currentTime = timeInSeconds;
        });
        
        // Update last start positions if playing
        if (isPlaying) {
          const newStartPositions = new Map<number, number>();
          audioElements.forEach((audio, playerNumber) => {
            newStartPositions.set(playerNumber, audio.currentTime);
          });
          setLastStartPositions(newStartPositions);
        }
      }
    }
  };

  // Create inline text stamp input (similar to editTextStamp but for new stamps)
  const createInlineTextStampInput = (wasEditorFocused: boolean = false, brElement: HTMLBRElement | null = null) => {
    if (!editableRef.current) {
      return;
    }
    
    // Only mark as from hotkey if editor was NOT focused (so we blur/save after)
    // If editor was already focused, user wants to continue editing
    isTextStampFromHotkeyRef.current = !wasEditorFocused;
    
    // Capture current timestamp
    const audio = audioElements.get(trackNumber);
    const preciseTime = audio ? audio.currentTime : 0;
    const timestamp = formatTime(preciseTime);
    
    // Create an input element
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Enter text stamp...';
    input.setAttribute('data-text-stamp-input', 'true'); // Mark as text stamp input
    input.style.cssText = `
      background: #4a5568;
      color: #f7fafc;
      border: 1px solid #718096;
      border-radius: 6px;
      padding: 8px 12px;
      font-size: inherit;
      font-family: inherit;
      min-width: 120px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    `;
    
    // Add focus styling
    input.addEventListener('focus', () => {
      input.style.borderColor = '#3182ce';
      input.style.boxShadow = '0 0 0 1px #3182ce';
    });
    
    input.addEventListener('blur', () => {
      input.style.borderColor = '#718096';
      input.style.boxShadow = 'none';
    });
    
    // Check if the editor is currently focused
    const isEditorFocused = document.activeElement === editableRef.current;
    
    // Check for meaningful content first (before any insertion logic)
    const hasContent = hasMeaningfulContent(editableRef.current);
    
    // If there's no meaningful content, clean up any empty elements (like <br> tags left by browser)
    // This should happen regardless of focus state to ensure clean insertion
    if (!hasContent) {
      // Remove all <br> tags and empty divs
      const brElements = editableRef.current.querySelectorAll('br');
      brElements.forEach(br => br.remove());
      const emptyDivs = Array.from(editableRef.current.querySelectorAll('div')).filter(div => {
        const text = div.textContent?.trim() || '';
        return text === '' && div.children.length === 0;
      });
      emptyDivs.forEach(div => div.remove());
      // Also check if the editor itself only contains empty elements
      if (editableRef.current.childNodes.length === 0 || 
          (editableRef.current.childNodes.length === 1 && editableRef.current.firstChild?.nodeName === 'BR')) {
        editableRef.current.innerHTML = '';
      }
    }
    
    // If editor is not focused and no brElement was provided, add a line break if there's content
    if (!isEditorFocused && !brElement) {
      // If there's existing meaningful content, add a line break
      if (hasContent) {
        // Ensure cursor is positioned at the end of the content
        const range = document.createRange();
        const selection = window.getSelection();
        
        // Move cursor to the very end of the editor content
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
        
        // Add a line break when editor is NOT focused and there's content
        // But only if there isn't already a <br> at the end
        const lastChild = editableRef.current.lastChild;
        if (!lastChild || lastChild.nodeName !== 'BR') {
          brElement = document.createElement('br');
          editableRef.current.appendChild(brElement);
          
          // Update the range to be after the new line break
          range.selectNodeContents(editableRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
    }
    
    // If a br element was just added (or was provided), insert the input after it
    if (brElement && brElement.parentNode === editableRef.current) {
      // Insert the input after the br element
      const range = document.createRange();
      range.setStartAfter(brElement);
      range.collapse(true);
      range.insertNode(input);
      
      // Move cursor after the input
      const newRange = document.createRange();
      newRange.setStartAfter(input);
      newRange.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(newRange);
    } else {
      // Insert the input at the current cursor position or at the end
      const selection = window.getSelection();
      
      // Ensure we have a valid selection within the editable area
      let validRange = null;
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        // Check if the selection is within the editable area
        if (editableRef.current && editableRef.current.contains(range.commonAncestorContainer)) {
          validRange = range;
        }
      }
      
      if (validRange) {
        // No need to add spaces - let the browser handle natural text flow
        validRange.insertNode(input);
      } else {
        // Fallback: append to end
        // Safety check: if the last child is a <br> and there's no meaningful content, remove it before appending
        if (!hasContent && editableRef.current?.lastChild?.nodeName === 'BR') {
          editableRef.current.lastChild.remove();
        }
        editableRef.current?.appendChild(input);
      }
    }
    
    // Focus the input immediately and use requestAnimationFrame to ensure it gets focus
    // even if the editableRef blur handler tries to interfere
    requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
    
    let isFinishing = false;
    let blurHandler: (() => void) | null = null;
    const finishTextStamp = () => {
      if (isFinishing) return;
      // Check if input still exists in DOM
      if (!input.parentNode) return;
      isFinishing = true;
      
      // Set flag to prevent blur handler from firing
      isFinishingTextStampRef.current = true;
      
      // Remove blur listener to prevent double execution
      if (blurHandler) {
        input.removeEventListener('blur', blurHandler);
        blurHandler = null;
      }
      
      const textContent = input.value.trim();
      if (textContent) {
        // Create the chord element
        const chordElement = document.createElement('span');
        chordElement.className = 'chord';
        chordElement.textContent = textContent;
        chordElement.style.color = '#FF8C00';
        chordElement.style.cursor = 'pointer';
        chordElement.style.textDecoration = 'none';
        chordElement.contentEditable = 'false';
        chordElement.style.userSelect = 'none';
        chordElement.onclick = (e) => {
          e.preventDefault();
          // Use the precise time stored in data-timestamp
          const preciseTime = parseFloat(chordElement.getAttribute('data-timestamp') || '0');
          
          // Set time for all audio elements
          audioElements.forEach(audio => {
            audio.currentTime = preciseTime;
          });
        };
        
        // Store the precise timestamp data for click handling
        chordElement.setAttribute('data-timestamp', preciseTime.toString());
        
        // Replace input with the chord element
        const parent = input.parentNode;
        if (parent && parent.contains(input)) {
          try {
            parent.replaceChild(chordElement, input);
            // Insert space after chord element (same as timestamp logic)
            const newRange = document.createRange();
            newRange.setStartAfter(chordElement);
            const space = document.createTextNode('\u00A0'); // Non-breaking space
            newRange.insertNode(space);
            // Move cursor after space
            newRange.setStartAfter(space);
            newRange.collapse(true);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(newRange);
            
            // Trigger input event to update state
            const event = new Event('input', { bubbles: true });
            editableRef.current?.dispatchEvent(event);
            
            // Re-attach event handlers for the new chord element
            attachEventHandlers();
            
            // Rebuild timestamp cache after inserting
            rebuildTimestampCache();
          } catch (error) {
            console.warn('Error replacing input with chord element:', error);
            // Fallback: just append the chord element
            parent.appendChild(chordElement);
            // Insert space after chord element (same as timestamp logic)
            const newRange = document.createRange();
            newRange.setStartAfter(chordElement);
            const space = document.createTextNode('\u00A0'); // Non-breaking space
            newRange.insertNode(space);
            // Move cursor after space
            newRange.setStartAfter(space);
            newRange.collapse(true);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(newRange);
            
            // Trigger input event to update state
            const event = new Event('input', { bubbles: true });
            editableRef.current?.dispatchEvent(event);
            
            // Re-attach event handlers for the new chord element
            attachEventHandlers();
            
            // Rebuild timestamp cache after inserting
            rebuildTimestampCache();
          }
        } else {
          // Fallback: just append the chord element
          editableRef.current?.appendChild(chordElement);
          // Insert space after chord element (same as timestamp logic)
          const newRange = document.createRange();
          newRange.setStartAfter(chordElement);
          const space = document.createTextNode('\u00A0'); // Non-breaking space
          newRange.insertNode(space);
          // Move cursor after space
          newRange.setStartAfter(space);
          newRange.collapse(true);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(newRange);
          
          // Trigger input event to update state
          const event = new Event('input', { bubbles: true });
          editableRef.current?.dispatchEvent(event);
          
          // Re-attach event handlers for the new chord element
          attachEventHandlers();
          
          // Rebuild timestamp cache after inserting
          rebuildTimestampCache();
        }
        
        // Reset the flag after DOM manipulation is complete
        // Use a short delay (50ms) that's less than the blur handler's 100ms timeout
        // This prevents the input event from immediately triggering a save,
        // but allows the blur handler to work normally when it checks after 100ms
        setTimeout(() => {
          isFinishingTextStampRef.current = false;
        }, 50);
        
        // If this textstamp was added via hotkey, return focus to main page
        if (isTextStampFromHotkeyRef.current) {
          isTextStampFromHotkeyRef.current = false;
          // Blur the editable area to remove focus from the text area
          // The flag will be reset by the timeout above, allowing the blur handler to save
          editableRef.current?.blur();
          // Focus on the content area to allow hotkeys to work again
          contentRef.current?.focus();
        }
      } else {
        // Remove input if no text entered
        input.remove();
        
        // Reset the flag immediately since we're not saving anything
        isFinishingTextStampRef.current = false;
        
        // If this was from hotkey but no text entered, still return focus
        if (isTextStampFromHotkeyRef.current) {
          isTextStampFromHotkeyRef.current = false;
          editableRef.current?.blur();
          contentRef.current?.focus();
        }
      }
    };
    
    // Handle Enter key and blur
    input.addEventListener('keydown', (e) => {
      console.log('Input keydown event:', e.key, 'target:', e.target);
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Enter key pressed, finishing text stamp');
        // Remove blur listener before finishing to prevent double execution
        if (blurHandler) {
          input.removeEventListener('blur', blurHandler);
          blurHandler = null;
        }
        // Use setTimeout to ensure the event is fully processed
        setTimeout(() => {
          finishTextStamp();
        }, 0);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Remove blur listener before removing input
        if (blurHandler) {
          input.removeEventListener('blur', blurHandler);
          blurHandler = null;
        }
        input.remove();
        // If this was from hotkey but cancelled, still return focus
        if (isTextStampFromHotkeyRef.current) {
          isTextStampFromHotkeyRef.current = false;
          editableRef.current?.blur();
          contentRef.current?.focus();
        }
      }
    }, true); // Use capture phase to ensure we catch the event first
    
    // Set up blur handler
    blurHandler = () => {
      // Only finish if not already finishing (Enter key might have triggered it)
      if (!isFinishing) {
        finishTextStamp();
      }
    };
    input.addEventListener('blur', blurHandler);
  };

  // Start editing and insert chord (for global hotkey)
  const startEditingAndInsertChord = () => {
    // Check if editor is currently focused BEFORE we do anything
    const wasEditorFocused = document.activeElement === editableRef.current;
    
    if (!isEditing) {
      const hasContent = note && note.trim().length > 0;
      
      setEditContent(note);
      setOriginalContent(note); // Store original for cancel
      setLiveCharCount(calculateLiveCharCount(note));
      setIsEditing(true);
      // Focus and create inline input after it renders
      setTimeout(() => {
        if (editableRef.current) {
          // Set content directly via DOM manipulation (like NotesModal)
          editableRef.current.innerHTML = note;
          
          // If editor wasn't focused and there's existing content, add <br>
          // But only if there isn't already a <br> at the end
          let brElement: HTMLBRElement | null = null;
          if (!wasEditorFocused && hasContent) {
            const lastChild = editableRef.current.lastChild;
            // Check if the last child is not already a <br> element
            if (!lastChild || lastChild.nodeName !== 'BR') {
              brElement = document.createElement('br');
              editableRef.current.appendChild(brElement);
            }
          }
          
          editableRef.current.focus();
          
          // Place cursor at the end of existing content
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editableRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
          
          // Create inline text stamp input after another small delay
          // Pass wasEditorFocused and brElement so it knows whether to insert after <br>
          setTimeout(() => {
            createInlineTextStampInput(wasEditorFocused, brElement);
          }, 10);
        }
      }, 50);
    } else {
      // Already editing, check if we need to add a new line
      if (!editableRef.current) return;
      
      const isEditorFocused = document.activeElement === editableRef.current;
      let brElement: HTMLBRElement | null = null;
      
      // Add a line break if editor is NOT focused AND there's existing meaningful content
      // But only if there isn't already a <br> at the end
      if (!isEditorFocused && hasMeaningfulContent(editableRef.current)) {
        const lastChild = editableRef.current.lastChild;
        // Check if the last child is not already a <br> element
        if (!lastChild || lastChild.nodeName !== 'BR') {
          brElement = document.createElement('br');
          editableRef.current.appendChild(brElement);
          
          // Move cursor to the end after adding the line break
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editableRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
      
      // Pass whether editor was focused - if it was, user wants to continue editing
      // Also pass the br element if one was just added, so the input can be inserted after it
      createInlineTextStampInput(wasEditorFocused, brElement);
    }
  };

  // Handle save (auto-save on blur)
  const handleSave = () => {
    // Prevent double saves
    if (isSavingRef.current) {
      return;
    }
    
    if (editableRef.current) {
      isSavingRef.current = true;
      
      let content = editableRef.current.innerHTML;
      
      // Trim whitespace from the content (similar to NotesModal logic)
      content = content.replace(/(<div\s*><br\s*\/?><\/div>|<br\s*\/?>|&nbsp;|\s)+$/gi, '');
      content = content.replace(/^(<div\s*><br\s*\/?><\/div>|<br\s*\/?>|&nbsp;|\s)+/gi, '');
      
      // Only save and show toast if content actually changed
      const trimmedOriginal = originalContent.replace(/(<div\s*><br\s*\/?><\/div>|<br\s*\/?>|&nbsp;|\s)+$/gi, '').replace(/^(<div\s*><br\s*\/?><\/div>|<br\s*\/?>|&nbsp;|\s)+/gi, '');
      const hasChanged = content !== trimmedOriginal;
      
      if (hasChanged && fileId) {
        addNote(fileId, content);
        toast({
          title: 'Note saved',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
      
      setIsEditing(false);
      
      // Reset saving flag after a short delay
      setTimeout(() => {
        isSavingRef.current = false;
      }, 500);
    }
  };

  // Handle blur - auto-save when editable area loses focus
  const handleBlur = (e: React.FocusEvent) => {
    // Use setTimeout to allow focus to settle on the new element
    // This prevents issues when clicking buttons or other interactive elements
    setTimeout(() => {
      // Don't save if we're currently finishing a text stamp (to prevent double saves)
      // The flag is reset after 50ms, so by the time this runs (100ms), it should be false
      // unless we're in the middle of finishing a text stamp
      if (isFinishingTextStampRef.current) {
        return;
      }
      
      const activeElement = document.activeElement;
      
      // If focus returned to the editable area itself, don't save
      if (activeElement === editableRef.current) {
        return;
      }
      
      // Don't save if focus moved to the custom filename input field
      if (activeElement === nameInputRef.current) {
        return;
      }
      
      // Don't save if focus moved to an input element inside the editable area (text stamp input)
      if (activeElement && 
          activeElement.tagName === 'INPUT' && 
          editableRef.current?.contains(activeElement) &&
          (activeElement as HTMLElement).getAttribute('data-text-stamp-input') === 'true') {
        return;
      }
      
      // Save when focus moves away (including to buttons/inputs or outside the panel)
      handleSave();
    }, 100);
  };

  // Handle cancel (revert changes)
  const handleCancel = () => {
    if (editableRef.current) {
      // Revert to original content
      editableRef.current.innerHTML = originalContent;
      setEditContent(originalContent);
    }
    setIsEditing(false);
  };

  // Handle cursor positioning around text stamps
  const handleCursorPositioning = () => {
    const selection = window.getSelection();
    if (!selection || !editableRef.current) return;
    
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    
    // Check if cursor is inside a text stamp
    const textStamp = container.nodeType === Node.ELEMENT_NODE 
      ? container as Element
      : container.parentElement;
    
    if (textStamp && (textStamp.classList.contains('chord') || textStamp.classList.contains('timestamp'))) {
      // Move cursor to after the text stamp
      const newRange = document.createRange();
      newRange.setStartAfter(textStamp);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  };

  // Handle key presses in edit mode
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      // Cmd+Enter still saves manually (though it will also save on blur)
      handleSave();
    } else if (e.key === 'i' || e.key === 'I') {
      if (e.metaKey && !e.shiftKey) {
        e.preventDefault();
        insertTimestamp();
      } else if (e.metaKey && e.shiftKey) {
        e.preventDefault();
        // Editor is focused when called from keyboard shortcut (user is typing)
        createInlineTextStampInput(true);
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Handle cursor positioning after arrow key navigation
      setTimeout(handleCursorPositioning, 0);
    }
  };

  // Helper to check if any track is over 10 minutes
  const hasLongTracks = (audioElements: Map<number, HTMLAudioElement>): boolean => {
    for (const audio of audioElements.values()) {
      if (audio.duration && audio.duration > 600) { // 600 seconds = 10 minutes
        return true;
      }
    }
    return false;
  };

  // Format time as MM:SS or M:SS based on track lengths
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const useLeadingZero = hasLongTracks(audioElements);
    const minutesStr = useLeadingZero 
      ? minutes.toString().padStart(2, '0')
      : minutes.toString();
    return `${minutesStr}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper function to check if editor has meaningful content (not just empty elements or whitespace)
  const hasMeaningfulContent = (element: HTMLElement | null): boolean => {
    if (!element) return false;
    
    // Get innerHTML and strip it to check for actual content
    const html = element.innerHTML.trim();
    if (!html) return false;
    
    // Remove all <br> tags and empty divs to check for actual content
    // This handles cases where browser leaves behind empty elements after deletion
    const cleanedHtml = html.replace(/<br\s*\/?>/gi, '').replace(/<div\s*><\/div>/gi, '').replace(/<div\s*><br\s*\/?><\/div>/gi, '');
    if (!cleanedHtml.trim()) return false;
    
    // Create a temporary element to strip HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanedHtml;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Check if there's any meaningful text after trimming whitespace
    return textContent.trim().length > 0;
  };

  // Insert timestamp at cursor position
  const insertTimestamp = (customText?: string) => {
    const audio = audioElements.get(trackNumber);
    if (audio && editableRef.current) {
      const timestamp = formatTime(audio.currentTime);
      
      // Create the timestamp element
      const timestampElement = document.createElement('a');
      timestampElement.href = '#';
      timestampElement.className = customText ? 'chord' : 'timestamp';
      timestampElement.textContent = customText || `[${timestamp}]`;
      timestampElement.style.color = customText ? '#FF8C00' : '#4299E1';
      timestampElement.style.cursor = 'pointer';
      timestampElement.style.textDecoration = customText ? 'none' : 'underline';
      timestampElement.contentEditable = 'false';
      timestampElement.style.userSelect = 'none';
      timestampElement.onclick = (e) => {
        e.preventDefault();
        const [minutes, seconds] = timestamp.split(':').map(Number);
        const timeInSeconds = minutes * 60 + seconds;
        
        // Set time for all audio elements
        audioElements.forEach(audio => {
          audio.currentTime = timeInSeconds;
        });
      };
      
      // Store the timestamp data for click handling (both chords and timestamps)
      const [minutes, seconds] = timestamp.split(':').map(Number);
      const timeInSeconds = minutes * 60 + seconds;
      timestampElement.setAttribute('data-timestamp', timeInSeconds.toString());

      const selection = window.getSelection();
      let validRange = null;
      
      // Ensure we have a valid selection within the editable area
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        // Check if the selection is within the editable area
        if (editableRef.current && editableRef.current.contains(range.commonAncestorContainer)) {
          validRange = range;
        }
      }

      // Ensure exactly one space before timestamp
      if (validRange && validRange.collapsed) {
        if (validRange.startOffset > 0) {
          const prevChar = validRange.startContainer.textContent?.charAt(validRange.startOffset - 1);
          if (prevChar && prevChar !== ' ' && prevChar !== '\u00A0') {
            validRange.insertNode(document.createTextNode(' '));
            validRange.setStart(validRange.startContainer, validRange.startOffset + 1);
          }
        }
      }

      if (validRange) {
        // Insert timestamp
        validRange.insertNode(timestampElement);
        // Insert space after timestamp
        const space = document.createTextNode('\u00A0'); // Non-breaking space
        validRange.setStartAfter(timestampElement);
        validRange.insertNode(space);
        // Move cursor after space
        validRange.setStartAfter(space);
        validRange.collapse(true);
        // Update selection
        selection?.removeAllRanges();
        selection?.addRange(validRange);
      } else {
        // If no valid selection, append to end
        if (editableRef.current.lastChild && editableRef.current.lastChild.nodeType === Node.TEXT_NODE) {
          const text = editableRef.current.lastChild.textContent || '';
          if (text[text.length - 1] && text[text.length - 1] !== ' ') {
            editableRef.current.appendChild(document.createTextNode(' '));
          }
        }
        editableRef.current.appendChild(timestampElement);
        editableRef.current.appendChild(document.createTextNode('\u00A0'));
        // Move cursor to end
        const newRange = document.createRange();
        newRange.selectNodeContents(editableRef.current);
        newRange.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(newRange);
      }
      
      // Rebuild timestamp cache after inserting
      rebuildTimestampCache();
      // Trigger input event to update state
      const event = new Event('input', { bubbles: true });
      editableRef.current.dispatchEvent(event);
    }
  };

  // Edit a text stamp (timestamp or chord) inline
  const editTextStamp = (element: HTMLElement) => {
    const isChord = element.classList.contains('chord');
    // For chords, get the text directly (no brackets to remove)
    // For timestamps, remove brackets if they exist
    const currentText = isChord ? (element.textContent || '') : (element.textContent?.replace(/[[\]]/g, '') || '');
    
    // Create an input element
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentText;
    input.style.cssText = `
      background: #4a5568;
      color: #f7fafc;
      border: 1px solid #718096;
      border-radius: 6px;
      padding: 8px 12px;
      font-size: inherit;
      font-family: inherit;
      min-width: 80px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    `;
    
    // Add focus styling
    input.addEventListener('focus', () => {
      input.style.borderColor = '#3182ce';
      input.style.boxShadow = '0 0 0 1px #3182ce';
    });
    
    input.addEventListener('blur', () => {
      input.style.borderColor = '#718096';
      input.style.boxShadow = 'none';
    });
    
    // Replace the element with the input
    const parent = element.parentNode;
    if (parent) {
      parent.replaceChild(input, element);
      input.focus();
      input.select();
      
      const finishEdit = () => {
        const newText = input.value.trim();
        if (newText && newText !== currentText) {
          // Update the text content of the existing element
          element.textContent = newText;
          // Restore the element to the DOM
          parent.replaceChild(element, input);
          
          // Trigger input event to update state
          const event = new Event('input', { bubbles: true });
          if (contentRef.current) {
            contentRef.current.dispatchEvent(event);
          }
          
          // Re-attach event handlers for the edited element
          attachEventHandlers();
          
          // Rebuild timestamp cache after editing (in case timestamp was changed)
          rebuildTimestampCache();
        } else {
          // Restore original element if no change or empty
          parent.replaceChild(element, input);
        }
      };
      
      // Handle Enter key and blur
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finishEdit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          parent.replaceChild(element, input);
        }
      });
      
      input.addEventListener('blur', finishEdit);
    }
  };
  
  // Set up click handlers for timestamps and chords
  useEffect(() => {
    if (contentRef.current) {
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
          e.preventDefault();
          // Always use the precise time stored in data-timestamp for both timestamps and chords
          const preciseTime = parseFloat(target.getAttribute('data-timestamp') || '0');
          
          if (preciseTime >= 0) {
            // Set time for all audio elements
            audioElements.forEach(audio => {
              audio.currentTime = preciseTime;
            });
            
            // Update last start positions if playing
            if (isPlaying) {
              const newStartPositions = new Map<number, number>();
              audioElements.forEach((audio, playerNumber) => {
                newStartPositions.set(playerNumber, audio.currentTime);
              });
              setLastStartPositions(newStartPositions);
            }
          }
        }
      };

      const handleDoubleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
          e.preventDefault();
          e.stopPropagation();
          editTextStamp(target);
        }
      };

      const handleTouchEnd = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
          e.preventDefault();
          const timestamp = target.textContent?.replace(/[[\]]/g, '');
          if (timestamp) {
            // For chords, we need to get the timestamp from the data attribute
            let timeInSeconds;
            if (target.classList.contains('chord')) {
              timeInSeconds = parseFloat(target.getAttribute('data-timestamp') || '0');
            } else {
              const [minutes, seconds] = timestamp.split(':').map(Number);
              timeInSeconds = minutes * 60 + seconds;
            }
            
            // Set time for all audio elements
            audioElements.forEach(audio => {
              audio.currentTime = timeInSeconds;
            });
            
            // Update last start positions if playing
            if (isPlaying) {
              const newStartPositions = new Map<number, number>();
              audioElements.forEach((audio, playerNumber) => {
                newStartPositions.set(playerNumber, audio.currentTime);
              });
              setLastStartPositions(newStartPositions);
            }
          }
        }
      };

      attachEventHandlers();

      return () => {
        if (contentRef.current) {
          contentRef.current.removeEventListener('click', handleClick);
          contentRef.current.removeEventListener('dblclick', handleTimestampDoubleClick);
          contentRef.current.removeEventListener('touchend', handleTouchEnd);
        }
      };
    }
  }, [audioElements, isPlaying, setLastStartPositions]);

  // Rebuild timestamp cache when note content changes
  const rebuildTimestampCache = () => {
    const areas = [editableRef.current, contentRef.current].filter(Boolean);
    const allElements: HTMLElement[] = [];
    
    areas.forEach(area => {
      if (area) {
        const chordElements = area.querySelectorAll('.chord');
        const timestampElements = area.querySelectorAll('.timestamp');
        chordElements.forEach(el => allElements.push(el as HTMLElement));
        timestampElements.forEach(el => allElements.push(el as HTMLElement));
      }
    });
    
    // Build sorted array of timestamps
    const timestamps: { element: HTMLElement; time: number; type: 'chord' | 'timestamp' }[] = [];
    allElements.forEach((element) => {
      const timestamp = element.getAttribute('data-timestamp');
      if (timestamp) {
        const time = parseFloat(timestamp);
        const type = element.classList.contains('chord') ? 'chord' : 'timestamp';
        timestamps.push({ element, time, type });
      } else {
        console.warn('[Highlighting] Element missing data-timestamp:', element.textContent, element);
      }
    });
    
    // Sort by timestamp
    timestamps.sort((a, b) => a.time - b.time);
    sortedTimestampsRef.current = timestamps;
    currentHighlightedIndexRef.current = -1; // Reset highlighted index when cache is rebuilt
    console.log('[Highlighting] Cache rebuilt:', {
      totalTimestamps: timestamps.length,
      timestamps: timestamps.map(t => ({ time: t.time.toFixed(2), type: t.type, text: t.element.textContent?.substring(0, 20) }))
    });
  };

  // Rebuild cache when note is first loaded or when editing starts
  // We don't rebuild on every keystroke - only when timestamps are actually added/edited
  useEffect(() => {
    // Rebuild when note is loaded (in case it has timestamps)
    if (note) {
      rebuildTimestampCache();
    }
  }, [note]); // Only when note changes, not on every editContent change

  // Update chord highlighting during playback - optimized to only update on boundary crossings
  useEffect(() => {
    if (!isPlaying) {
      // Clear highlighting when not playing
      const clearChordHighlighting = () => {
        sortedTimestampsRef.current.forEach(({ element, type }) => {
          element.style.backgroundColor = 'transparent';
          if (type === 'chord') {
            element.style.color = '#FF8C00'; // Original orange for chords
          } else {
            element.style.color = '#4299E1'; // Original blue for timestamps
          }
          element.style.borderRadius = '0';
          element.style.padding = '0';
        });
      };
      
      clearChordHighlighting();
      currentHighlightedIndexRef.current = -1;
      return;
    }

    // Check if we have either editableRef or contentRef available
    if (!editableRef.current && !contentRef.current) {
      return;
    }

    // Rebuild cache if empty
    if (sortedTimestampsRef.current.length === 0) {
      rebuildTimestampCache();
    }

    const currentAudio = audioElements.get(trackNumber);
    if (!currentAudio) {
      return;
    }

    // Store reference for TypeScript
    const audioElement = currentAudio;

    // Find which element should be highlighted based on current time
    const findHighlightedIndex = (currentTime: number): number => {
      for (let i = 0; i < sortedTimestampsRef.current.length; i++) {
        const elementTime = sortedTimestampsRef.current[i].time;
        const nextElementTime = i < sortedTimestampsRef.current.length - 1 
          ? sortedTimestampsRef.current[i + 1].time 
          : Infinity;
        
        if (currentTime >= elementTime && currentTime < nextElementTime) {
          return i;
        }
      }
      return -1;
    };

    // Update highlighting only when crossing a boundary
    const updateHighlighting = () => {
      const currentTime = audioElement.currentTime;
      const newHighlightedIndex = findHighlightedIndex(currentTime);
      
      // Only update if the highlighted index has changed
      if (newHighlightedIndex !== currentHighlightedIndexRef.current) {
        console.log('[Highlighting] Boundary crossed:', {
          currentTime: currentTime.toFixed(2),
          oldIndex: currentHighlightedIndexRef.current,
          newIndex: newHighlightedIndex,
          totalTimestamps: sortedTimestampsRef.current.length
        });
        // Clear previous highlight
        if (currentHighlightedIndexRef.current >= 0 && 
            currentHighlightedIndexRef.current < sortedTimestampsRef.current.length) {
          const prevItem = sortedTimestampsRef.current[currentHighlightedIndexRef.current];
          prevItem.element.style.backgroundColor = 'transparent';
          if (prevItem.type === 'chord') {
            prevItem.element.style.color = '#FF8C00';
          } else {
            prevItem.element.style.color = '#4299E1';
          }
          prevItem.element.style.borderRadius = '0';
          prevItem.element.style.padding = '0';
        }
        
        // Apply new highlight
        if (newHighlightedIndex >= 0) {
          const newItem = sortedTimestampsRef.current[newHighlightedIndex];
          newItem.element.style.backgroundColor = '#FFD700';
          newItem.element.style.color = '#000000';
          newItem.element.style.borderRadius = '3px';
          newItem.element.style.padding = '1px 3px';
          
          // Scroll the highlighted element into view
          newItem.element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
        
        currentHighlightedIndexRef.current = newHighlightedIndex;
      }
    };

    // Update immediately
    updateHighlighting();
    
    // Use a small interval to check frequently (every 100ms) but only update DOM on boundary crossings
    // This is more efficient than requestAnimationFrame (60fps) but still catches all boundaries
    const checkInterval = setInterval(() => {
      if (!audioElement.paused) {
        updateHighlighting();
      }
    }, 100);
    
    // Also listen to timeupdate as a backup (fires ~4 times per second)
    if (audioElement.addEventListener) {
      audioElement.addEventListener('timeupdate', updateHighlighting);
    }
    
    return () => {
      clearInterval(checkInterval);
      // Get currentAudio again in cleanup to ensure it's still available
      const audio = audioElements.get(trackNumber);
      if (audio && audio.removeEventListener) {
        audio.removeEventListener('timeupdate', updateHighlighting);
      }
      // Clean up cache rebuild timeout
      if (cacheRebuildTimeoutRef.current) {
        clearTimeout(cacheRebuildTimeoutRef.current);
        cacheRebuildTimeoutRef.current = null;
      }
    };
  }, [isPlaying, trackNumber, audioElements, note, isEditing, editContent]);

  // Function to highlight a timestamp at a specific time (can be called even when not playing)
  const highlightTimestampAtTime = useCallback((time: number) => {
    // Check if we have either editableRef or contentRef available
    if (!editableRef.current && !contentRef.current) {
      return;
    }

    // Rebuild cache if empty
    if (sortedTimestampsRef.current.length === 0) {
      rebuildTimestampCache();
    }

    // Find which element should be highlighted based on the given time
    const findHighlightedIndex = (targetTime: number): number => {
      for (let i = 0; i < sortedTimestampsRef.current.length; i++) {
        const elementTime = sortedTimestampsRef.current[i].time;
        const nextElementTime = i < sortedTimestampsRef.current.length - 1 
          ? sortedTimestampsRef.current[i + 1].time 
          : Infinity;
        
        if (targetTime >= elementTime && targetTime < nextElementTime) {
          return i;
        }
      }
      return -1;
    };

    const newHighlightedIndex = findHighlightedIndex(time);

    // Clear previous highlight
    if (currentHighlightedIndexRef.current >= 0 && 
        currentHighlightedIndexRef.current < sortedTimestampsRef.current.length) {
      const prevItem = sortedTimestampsRef.current[currentHighlightedIndexRef.current];
      prevItem.element.style.backgroundColor = 'transparent';
      if (prevItem.type === 'chord') {
        prevItem.element.style.color = '#FF8C00';
      } else {
        prevItem.element.style.color = '#4299E1';
      }
      prevItem.element.style.borderRadius = '0';
      prevItem.element.style.padding = '0';
    }

    // Apply new highlight
    if (newHighlightedIndex >= 0) {
      const newItem = sortedTimestampsRef.current[newHighlightedIndex];
      newItem.element.style.backgroundColor = '#FFD700';
      newItem.element.style.color = '#000000';
      newItem.element.style.borderRadius = '3px';
      newItem.element.style.padding = '1px 3px';
      
      // Scroll the highlighted element into view
      newItem.element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }

    currentHighlightedIndexRef.current = newHighlightedIndex;
  }, []);

  // Clear highlighting when editing stops or component unmounts
  useEffect(() => {
    return () => {
      // Clear highlighting from both editable and content areas
      const areas = [editableRef.current, contentRef.current].filter(Boolean);
      areas.forEach(area => {
        if (area) {
          const chordElements = area.querySelectorAll('.chord');
          const timestampElements = area.querySelectorAll('.timestamp');
          
          chordElements.forEach((chordElement) => {
            (chordElement as HTMLElement).style.backgroundColor = 'transparent';
            (chordElement as HTMLElement).style.color = '#FF8C00'; // Original orange
            (chordElement as HTMLElement).style.borderRadius = '0';
            (chordElement as HTMLElement).style.padding = '0';
          });
          
          timestampElements.forEach((timestampElement) => {
            (timestampElement as HTMLElement).style.backgroundColor = 'transparent';
            (timestampElement as HTMLElement).style.color = '#4299E1'; // Original blue
            (timestampElement as HTMLElement).style.borderRadius = '0';
            (timestampElement as HTMLElement).style.padding = '0';
          });
        }
      });
    };
  }, []);
  
  // Check if note has meaningful content (strip HTML and check for actual text)
  const hasContent = () => {
    if (!note) return false;
    // Create a temporary element to strip HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = note;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    // Check if there's any meaningful text after trimming whitespace
    return textContent.trim().length > 0;
  };

  // Make the editing functions available globally for hotkey access
  useEffect(() => {
    // Set a global reference to this component's functions
    window.__expandedNotePanel = {
      trackNumber,
      startEditing,
      startEditingAndInsertTimestamp,
      startEditingAndInsertChord,
      insertTimestampAtEnd,
      isEditing,
      highlightTimestampAtTime
    };
    
    return () => {
      // Clean up when component unmounts
      if (window.__expandedNotePanel?.trackNumber === trackNumber) {
        delete window.__expandedNotePanel;
      }
    };
  }, [trackNumber, startEditing, startEditingAndInsertTimestamp, startEditingAndInsertChord, insertTimestampAtEnd, isEditing, highlightTimestampAtTime]);

  return (
    <>
      <Box
        w="100%"
        h={containerHeight}
        bg="gray.800"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.700"
        pt={1}
        px={4}
        pb={0.5}
        mb={4}
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
        display="flex"
        flexDirection="column"
        data-expanded-note-panel
      >
        <HStack justify="space-between" align="center" mb={1}>
          {isEditing ? (
            <HStack spacing={2} flex="1">
              <Tooltip
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Timestamp</Text>
                    {!isMobile && <TooltipKeyBox>⌘I</TooltipKeyBox>}
                  </HStack>
                }
                placement="top"
              >
                <IconButton
                  aria-label="Timestamp"
                  icon={<FaClock style={{ fontSize: '1.2em' }} />}
                  size="xs"
                  variant="ghost"
                  color="yellow.400"
                  onClick={() => insertTimestamp()}
                  _hover={{ 
                    bg: "rgba(255, 255, 0, 0.1)",
                    color: "yellow.300",
                    transform: "scale(1.05)"
                  }}
                  transition="all 0.2s ease-in-out"
                />
              </Tooltip>
              <Tooltip
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Textstamp</Text>
                    {!isMobile && <TooltipKeyBox>⇧⌘I</TooltipKeyBox>}
                  </HStack>
                }
                placement="top"
              >
                <IconButton
                  aria-label="Textstamp"
                  icon={<FaPen />}
                  size="xs"
                  variant="ghost"
                  color="yellow.400"
                  onClick={() => {
                    // Button is only visible when editing, so if user clicks it while editing,
                    // they want to continue editing (don't blur/save)
                    // We assume editor was focused since they're in editing mode
                    createInlineTextStampInput(true);
                  }}
                  _hover={{ 
                    bg: "rgba(255, 255, 0, 0.1)",
                    color: "yellow.300",
                    transform: "scale(1.05)"
                  }}
                  transition="all 0.2s ease-in-out"
                />
              </Tooltip>
              <Input
                ref={nameInputRef}
                value={displayName}
                onChange={handleDisplayNameChange}
                onKeyDown={handleDisplayNameKeyDown}
                onBlur={handleDisplayNameSave}
                size="xs"
                variant="outline"
                fontSize="sm"
                color="accent.400"
                fontWeight="normal"
                placeholder="Track name..."
                _placeholder={{ color: "gray.500" }}
                bg="gray.750"
                borderColor="gray.600"
                px={2}
                borderRadius="md"
                _hover={{
                  borderColor: "gray.500"
                }}
                _focus={{
                  outline: "none",
                  borderColor: "accent.400",
                  boxShadow: "0 0 0 1px var(--chakra-colors-accent-400)"
                }}
                flex="1"
                minW="0"
              />
            </HStack>
          ) : (
            <Text 
              fontSize="sm" 
              color="accent.400"
            >
              Notes: {!isMobile && hasContent() && <Text as="span" color="gray.500" fontSize="xs">(double-click to edit)</Text>}
            </Text>
          )}
          {!isEditing && (
            <Tooltip
              label={
                <HStack spacing={2} color="gray.800">
                  <Text>Edit Notes</Text>
                  {!isMobile && <TooltipKeyBox>N</TooltipKeyBox>}
                </HStack>
              }
              placement="top"
            >
              <IconButton
                aria-label="Edit Note"
                icon={<FaEdit />}
                size="sm"
                variant="ghost"
                color="#ffa726"
                onClick={isMobile ? onOpen : startEditing}
                _hover={{ 
                  bg: "rgba(255, 167, 38, 0.1)",
                  color: "#ffa726",
                  transform: "scale(1.05)"
                }}
                transition="all 0.2s ease-in-out"
              />
            </Tooltip>
          )}
        </HStack>
        {isEditing ? (
          <Box flex="1" display="flex" flexDirection="column" minH="0">
            <Box 
              ref={editableRef}
              contentEditable
              suppressContentEditableWarning
              onKeyDown={handleKeyDown}
              onClick={handleCursorPositioning}
              onBlur={handleBlur}
              onInput={(e) => {
                const content = e.currentTarget.innerHTML;
                
                // Note: Cache is only rebuilt when timestamps are actually added/edited,
                // not on every keystroke. This is more efficient since regular text
                // changes don't affect the timestamp cache.
                
                // Calculate live character count
                const newLiveCount = calculateLiveCharCount(content);
                setLiveCharCount(newLiveCount);
                
                // Calculate plain text length for the new content
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = content;
                const newPlainText = tempDiv.textContent || tempDiv.innerText || '';
                
                // Calculate plain text length for the current saved note
                const currentSavedNote = fileId ? getNote(fileId) : '';
                tempDiv.innerHTML = currentSavedNote;
                const currentPlainText = tempDiv.textContent || tempDiv.innerText || '';
                
                // Calculate the difference in characters
                const charDifference = newPlainText.length - currentPlainText.length;
                
                // Check if the change would exceed global limit
                if (charDifference > 0 && !canAddCharacters(charDifference)) {
                  // If exceeding limit, revert to previous content and restore previous count
                  e.currentTarget.innerHTML = editContent;
                  setLiveCharCount(calculateLiveCharCount(editContent));
                } else {
                  setEditContent(content);
                }
              }}
              fontSize="sm"
              color="gray.300"
              lineHeight="1.6"
              flex="1"
              overflowY="auto"
              p={2}
              border="2px solid"
              borderColor="accent.400"
              borderRadius="md"
              bg="gray.750"
              minH="0"
              _focus={{
                outline: "none",
                borderColor: "accent.300"
              }}
              css={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  bg: 'gray.900',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bg: 'gray.600',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  bg: 'gray.500',
                },
              }}
            />
            <Text fontSize="xs" color="gray.500" textAlign="right" mt={0.5}>
              {liveCharCount}/{GLOBAL_CHARACTER_LIMIT} characters
            </Text>
          </Box>
        ) : (
          <Box 
            ref={contentRef}
            fontSize="sm"
            color="gray.300"
            lineHeight="1.6"
            flex="1"
            overflowY="auto"
            cursor={isMobile ? "default" : "pointer"}
            onDoubleClick={handleDoubleClick}
            tabIndex={-1}
            _hover={!isMobile ? {
              bg: "gray.750",
              transition: "background-color 0.2s"
            } : {}}
            p={2}
            borderRadius="md"
            minH="0"
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                bg: 'gray.900',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                bg: 'gray.600',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                bg: 'gray.500',
              },
            }}
            dangerouslySetInnerHTML={{ __html: note || `<em style="color: #9CA3AF;">No notes yet. ${isMobile ? 'Click the Edit button to add some!' : 'Double-click here or click the Edit button to add some!'}</em>` }}
          />
        )}
      </Box>
      <NotesModal isOpen={isOpen} onClose={onClose} trackNumber={trackNumber} fileId={fileId} />
    </>
  );
};

export default ExpandedNotePanel; 